import React, { useState, useEffect } from 'react';
import { FaDollarSign, FaChartLine, FaFileInvoiceDollar, FaCalendarAlt, FaDownload, FaEye, FaCheck, FaClock, FaArrowUp, FaArrowDown, FaBox, FaCog, FaWrench } from 'react-icons/fa';
import Badge from '@/components/common/Badge';
import Card, { CardBody } from '@/components/common/Card';
import Button from '@/components/common/Button';
import Modal from '@/components/common/Modal';
import FilterBar from '@/components/common/FilterBar'; 
import CustomSelect from '@/components/common/CustomSelect';
import toastService from '@/utils/toastService';

/**
 * Dashboard de comisiones para el proveedor exclusivo teknoParts
 * Muestra reportes detallados de comisiones y análisis financiero
 */
const ComisionesProveedorDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [comisiones, setComisiones] = useState([]);
  const [filteredComisiones, setFilteredComisiones] = useState([]);
  const [selectedPeriodo, setSelectedPeriodo] = useState(null);
  const [showDetalleModal, setShowDetalleModal] = useState(false);
  const [resumenGeneral, setResumenGeneral] = useState({});

  // Filtros (sin filtro por proveedor ya que solo tenemos teknoParts)
  const [filters, setFilters] = useState({
    periodo: '2025-01', // Mes actual
    estado: '',
    searchTerm: ''
  });

  // Mock data de comisiones
  const mockComisiones = [
    // Enero 2025
    {
      id: 'COM-2025-001',
      periodo: '2025-01',
      fecha_venta: '2025-01-25T10:30:00Z',
      proveedor: {
        id: 1,
        nombre: 'teknoParts',
        logo: '/img/marketplace/teknoparts.png'
      },
      orden_compra_id: 'ORD-2025-001',
      mantenimiento_id: 'MANT-2025-045',
      monto_venta: 157000,
      porcentaje_comision: 5,
      monto_comision: 7850,
      estado_pago: 'pagado',
      fecha_pago: '2025-01-30T14:20:00Z',
      items_vendidos: [
        { nombre: 'Filtro de Aceite Toyota Premium', cantidad: 2, precio_unitario: 32000 },
        { nombre: 'Kit Pastillas de Freno Performance', cantidad: 1, precio_unitario: 85000 }
      ],
      metodo_pago: 'transferencia_bancaria',
      numero_factura: 'FC-2025-001'
    },
    {
      id: 'COM-2025-002',
      periodo: '2025-01',
      fecha_venta: '2025-01-26T14:20:00Z',
      proveedor: {
        id: 1,
        nombre: 'teknoParts',
        logo: '/img/marketplace/teknoparts.png'
      },
      orden_compra_id: 'ORD-2025-002',
      mantenimiento_id: 'MANT-2025-046',
      monto_venta: 125000,
      porcentaje_comision: 5,
      monto_comision: 6250,
      estado_pago: 'pagado',
      fecha_pago: '2025-01-30T14:20:00Z',
      items_vendidos: [
        { nombre: 'Amortiguador Delantero Izquierdo', cantidad: 1, precio_unitario: 125000 }
      ],
      metodo_pago: 'transferencia_bancaria',
      numero_factura: 'FC-2025-001'
    },
    {
      id: 'COM-2025-003',
      periodo: '2025-01',
      fecha_venta: '2025-01-28T11:00:00Z',
      proveedor: {
        id: 2,
        nombre: 'teknoParts',
        logo: '/img/marketplace/teknoparts.png'
      },
      orden_compra_id: 'ORD-2025-004',
      mantenimiento_id: null,
      monto_venta: 85000,
      porcentaje_comision: 5,
      monto_comision: 4250,
      estado_pago: 'pendiente',
      fecha_pago: null,
      items_vendidos: [
        { nombre: 'Bomba de Agua Universal', cantidad: 1, precio_unitario: 85000 }
      ]
    },
    // Febrero 2025
    {
      id: 'COM-2025-004',
      periodo: '2025-02',
      fecha_venta: '2025-02-02T09:15:00Z',
      proveedor: {
        id: 1,
        nombre: 'teknoParts',
        logo: '/img/marketplace/teknoparts.png'
      },
      orden_compra_id: 'ORD-2025-005',
      mantenimiento_id: 'MANT-2025-048',
      monto_venta: 95000,
      porcentaje_comision: 5,
      monto_comision: 4750,
      estado_pago: 'pendiente',
      fecha_pago: null,
      items_vendidos: [
        { nombre: 'Sensor de Temperatura Coolant', cantidad: 1, precio_unitario: 35000 },
        { nombre: 'Kit de Embrague', cantidad: 1, precio_unitario: 60000 }
      ]
    },
    {
      id: 'COM-2025-005',
      periodo: '2025-02',
      fecha_venta: '2025-02-05T16:30:00Z',
      proveedor: {
        id: 2,
        nombre: 'teknoParts',
        logo: '/img/marketplace/teknoparts.png'
      },
      orden_compra_id: 'ORD-2025-006',
      mantenimiento_id: 'MANT-2025-049',
      monto_venta: 210000,
      porcentaje_comision: 5,
      monto_comision: 10500,
      estado_pago: 'pendiente',
      fecha_pago: null,
      items_vendidos: [
        { nombre: 'Turbo Completo', cantidad: 1, precio_unitario: 180000 },
        { nombre: 'Filtro de Aire Deportivo', cantidad: 1, precio_unitario: 30000 }
      ]
    }
  ];

  const periodos = [
    { value: '2025-02', label: 'Febrero 2025' },
    { value: '2025-01', label: 'Enero 2025' },
    { value: '2024-12', label: 'Diciembre 2024' },
    { value: '2024-11', label: 'Noviembre 2024' }
  ];

  // Solo tenemos teknoParts como proveedor exclusivo

  const estadosPago = [
    { value: 'pendiente', label: 'Pendiente' },
    { value: 'pagado', label: 'Pagado' },
    { value: 'procesando', label: 'Procesando' }
  ];

  useEffect(() => {
    fetchComisiones();
  }, []);

  useEffect(() => {
    applyFilters();
    calcularResumen();
  }, [comisiones, filters]);

  const fetchComisiones = async () => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setComisiones(mockComisiones);
    } catch (error) {
      toastService.error('Error al cargar comisiones');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...comisiones];

    // Filtro por período
    if (filters.periodo) {
      filtered = filtered.filter(com => com.periodo === filters.periodo);
    }

    // Filtro por proveedor
    if (filters.proveedor) {
      filtered = filtered.filter(com => 
        com.proveedor.id.toString() === filters.proveedor
      );
    }

    // Filtro por estado
    if (filters.estado) {
      filtered = filtered.filter(com => com.estado_pago === filters.estado);
    }

    // Filtro por término de búsqueda
    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(com =>
        com.id.toLowerCase().includes(term) ||
        com.orden_compra_id.toLowerCase().includes(term) ||
        (com.mantenimiento_id && com.mantenimiento_id.toLowerCase().includes(term)) ||
        com.proveedor.nombre.toLowerCase().includes(term) ||
        com.items_vendidos.some(item => 
          item.nombre.toLowerCase().includes(term)
        )
      );
    }

    // Ordenar por fecha más reciente
    filtered.sort((a, b) => new Date(b.fecha_venta) - new Date(a.fecha_venta));

    setFilteredComisiones(filtered);
  };

  const calcularResumen = () => {
    const resumen = {
      totalComisiones: 0,
      comisionesPendientes: 0,
      comisionesPagadas: 0,
      ventasTotales: 0,
      transaccionesTotales: filteredComisiones.length,
      proveedoresActivos: new Set(),
      promedioComision: 0
    };

    filteredComisiones.forEach(com => {
      resumen.totalComisiones += com.monto_comision;
      resumen.ventasTotales += com.monto_venta;
      resumen.proveedoresActivos.add(com.proveedor.id);

      if (com.estado_pago === 'pendiente') {
        resumen.comisionesPendientes += com.monto_comision;
      } else if (com.estado_pago === 'pagado') {
        resumen.comisionesPagadas += com.monto_comision;
      }
    });

    resumen.proveedoresActivos = resumen.proveedoresActivos.size;
    resumen.promedioComision = resumen.transaccionesTotales > 0 
      ? resumen.totalComisiones / resumen.transaccionesTotales 
      : 0;

    setResumenGeneral(resumen);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleVerDetalle = (periodo) => {
    setSelectedPeriodo(periodo);
    setShowDetalleModal(true);
  };

  const handleExportarReporte = async (formato = 'excel') => {
    try {
      toastService.info(`Exportando reporte en formato ${formato}...`);
      // Simular export
      await new Promise(resolve => setTimeout(resolve, 2000));
      toastService.success(`Reporte exportado exitosamente`);
    } catch (error) {
      toastService.error('Error al exportar reporte');
    }
  };

  const formatFecha = (fecha) => {
    return new Date(fecha).toLocaleDateString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatPeriodo = (periodo) => {
    const [year, month] = periodo.split('-');
    const meses = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    return `${meses[parseInt(month) - 1]} ${year}`;
  };



  return (
    <div>
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 gap-4">
        <div className="flex items-center">
          <div className="w-1 h-6 rounded-full mr-3 bg-yellow-500" />
          <h1 className="text-xl lg:text-2xl font-bold">Dashboard de Comisiones</h1>
        </div>
        
        {/* Solo en escritorio: Stats inline con botones */}
        <div className="hidden lg:flex items-center flex-1 justify-center gap-3 mx-6">
          <Card className="bg-green-900/20 border-green-800/50 w-36">
            <CardBody className="px-4 py-2">
              <div className="text-center">
                <p className="text-xs text-gray-400 mb-1">Comisiones Totales</p>
                <div className="flex items-center justify-center">
                  <FaDollarSign className="text-green-400 text-xl mr-2" />
                  <p className="text-xl font-bold">${Math.round((resumenGeneral.totalComisiones || 0) / 1000)}k</p>
                </div>
              </div>
            </CardBody>
          </Card>

          <Card className="bg-yellow-900/20 border-yellow-800/50 w-36">
            <CardBody className="px-4 py-2">
              <div className="text-center">
                <p className="text-xs text-gray-400 mb-1">Pendientes</p>
                <div className="flex items-center justify-center">
                  <FaClock className="text-yellow-400 text-xl mr-2" />
                  <p className="text-xl font-bold">${Math.round((resumenGeneral.comisionesPendientes || 0) / 1000)}k</p>
                </div>
              </div>
            </CardBody>
          </Card>

          <Card className="bg-blue-900/20 border-blue-800/50 w-36">
            <CardBody className="px-4 py-2">
              <div className="text-center">
                <p className="text-xs text-gray-400 mb-1">Ventas Totales</p>
                <div className="flex items-center justify-center">
                  <FaChartLine className="text-blue-400 text-xl mr-2" />
                  <p className="text-xl font-bold">${Math.round((resumenGeneral.ventasTotales || 0) / 1000)}k</p>
                </div>
              </div>
            </CardBody>
          </Card>

          <Card className="bg-purple-900/20 border-purple-800/50 w-36">
            <CardBody className="px-4 py-2">
              <div className="text-center">
                <p className="text-xs text-gray-400 mb-1">Promedio</p>
                <div className="flex items-center justify-center">
                  <FaCalendarAlt className="text-purple-400 text-xl mr-2" />
                  <p className="text-xl font-bold">${Math.round((resumenGeneral.promedioComision || 0) / 1000)}k</p>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
        
        {/* Botones para escritorio */}
        <div className="hidden lg:flex gap-3">
          <Button
            variant="secondary"
            onClick={() => handleExportarReporte('excel')}
            icon={<FaDownload />}
          >
            Excel
          </Button>
          <Button
            variant="secondary"
            onClick={() => handleExportarReporte('pdf')}
            icon={<FaFileInvoiceDollar />}
          >
            PDF
          </Button>
        </div>
        
        {/* Solo en móvil/tablet: Botones separados */}
        <div className="lg:hidden flex gap-2">
          <Button
            variant="secondary"
            onClick={() => handleExportarReporte('excel')}
            icon={<FaDownload />}
            className="flex-1"
          >
            <span className="hidden sm:inline">Exportar Excel</span>
            <span className="sm:hidden">Excel</span>
          </Button>
          <Button
            variant="secondary"
            onClick={() => handleExportarReporte('pdf')}
            icon={<FaFileInvoiceDollar />}
            className="flex-1"
          >
            <span className="hidden sm:inline">Reporte PDF</span>
            <span className="sm:hidden">PDF</span>
          </Button>
        </div>
      </div>

      {/* Stats principales - solo en móvil/tablet */}
      <div className="lg:hidden grid grid-cols-2 gap-6 mb-6">
        <Card className="bg-green-900/20 border-green-800/50">
          <CardBody className="p-4">
            <div className="text-center">
              <p className="text-xs text-gray-400 mb-1">Comisiones Totales</p>
              <div className="flex items-center justify-center">
                <FaDollarSign className="text-green-400 text-xl mr-2" />
                <p className="text-xl font-bold">
                  ${(resumenGeneral.totalComisiones || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                </p>
              </div>
              <p className="text-xs text-green-300 mt-1">
                {resumenGeneral.transaccionesTotales || 0} transacciones
              </p>
            </div>
          </CardBody>
        </Card>

        <Card className="bg-yellow-900/20 border-yellow-800/50">
          <CardBody className="p-4">
            <div className="text-center">
              <p className="text-xs text-gray-400 mb-1">Pendiente de Pago</p>
              <div className="flex items-center justify-center">
                <FaClock className="text-yellow-400 text-xl mr-2" />
                <p className="text-xl font-bold">
                  ${(resumenGeneral.comisionesPendientes || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                </p>
              </div>
              <p className="text-xs text-yellow-300 mt-1">
                Por cobrar este período
              </p>
            </div>
          </CardBody>
        </Card>

        <Card className="bg-blue-900/20 border-blue-800/50">
          <CardBody className="p-4">
            <div className="text-center">
              <p className="text-xs text-gray-400 mb-1">Ventas Totales</p>
              <div className="flex items-center justify-center">
                <FaChartLine className="text-blue-400 text-xl mr-2" />
                <p className="text-xl font-bold">
                  ${(resumenGeneral.ventasTotales || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                </p>
              </div>
              <p className="text-xs text-blue-300 mt-1">
                En repuestos externos
              </p>
            </div>
          </CardBody>
        </Card>

        <Card className="bg-purple-900/20 border-purple-800/50">
          <CardBody className="p-4">
            <div className="text-center">
              <p className="text-xs text-gray-400 mb-1">Promedio Comisión</p>
              <div className="flex items-center justify-center">
                <FaCalendarAlt className="text-purple-400 text-xl mr-2" />
                <p className="text-xl font-bold">
                  ${Math.round(resumenGeneral.promedioComision || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                </p>
              </div>
              <p className="text-xs text-purple-300 mt-1">
                Por transacción
              </p>
            </div>
          </CardBody>
        </Card>
      </div>


      {/* Filtros */}
      <FilterBar
        onSearch={(value) => setFilters({...filters, searchTerm: value})}
        searchValue={filters.searchTerm}
        onReset={() => setFilters({
          periodo: '2025-01', proveedor: '', estado: '', searchTerm: ''
        })}
        searchPlaceholder="Buscar por ID, orden, mantenimiento o repuesto..."
      >
        <CustomSelect
          name="periodo"
          value={filters.periodo}
          onChange={handleFilterChange}
          options={periodos}
          className="w-full md:min-w-[150px] md:w-auto"
        />
        
        {/* Filtro de proveedor removido - solo teknoParts */}

        <CustomSelect
          name="estado"
          value={filters.estado}
          onChange={handleFilterChange}
          placeholder="Todos los estados"
          options={estadosPago}
          className="w-full md:min-w-[150px] md:w-auto"
        />
      </FilterBar>

      {/* Lista de comisiones */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filteredComisiones.map(comision => {
            // Determinar color del borde según estado de pago
            const getBorderColor = (estado) => {
              switch (estado) {
                case 'pagado':
                  return 'bg-green-500';
                case 'pendiente':
                  return 'bg-yellow-500';
                case 'procesando':
                  return 'bg-blue-500';
                default:
                  return 'bg-gray-500';
              }
            };

            return (
              <div key={comision.id} className="bg-gray-800 rounded-lg flex flex-col border border-gray-700 hover:border-gray-600 transition-all duration-200 shadow-sm relative overflow-hidden">
                <div className={`${getBorderColor(comision.estado_pago)} w-1 h-full absolute left-0 top-0 bottom-0 rounded-l-md`}></div>
                
                {/* Cuerpo de la tarjeta */}
                <div className="flex-grow p-4">
                  {/* Primera fila: ID y estado */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <FaDollarSign className="mr-2 text-green-400" />
                      <span className="font-medium">{comision.id}</span>
                      <span className="text-gray-400 ml-2 text-sm">
                        {formatFecha(comision.fecha_venta)}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Badge 
                        variant={comision.estado_pago === 'pagado' ? 'success' : 
                                comision.estado_pago === 'pendiente' ? 'warning' : 'info'}
                        size="sm"
                      >
                        {comision.estado_pago === 'pagado' && <FaCheck className="mr-1" />}
                        {comision.estado_pago === 'pendiente' && <FaClock className="mr-1" />}
                        {comision.estado_pago === 'pagado' ? 'Pagado' : 
                         comision.estado_pago === 'pendiente' ? 'Pendiente' : 'Procesando'}
                      </Badge>
                    </div>
                  </div>

                  {/* Segunda fila: información del proveedor y total */}
                  <div className="flex justify-between items-center mb-3">
                    <div className="flex flex-wrap items-center gap-2 flex-1">
                      {/* Proveedor */}
                      <div className="inline-flex items-center bg-gray-700 bg-opacity-50 rounded-md px-2 py-1">
                        <img 
                          src={comision.proveedor.logo} 
                          alt={comision.proveedor.nombre}
                          className="w-4 h-4 rounded mr-1"
                        />
                        <span className="font-medium text-sm">{comision.proveedor.nombre}</span>
                      </div>

                      {/* Orden de compra */}
                      <div className="inline-flex items-center bg-gray-700 bg-opacity-50 rounded-md px-2 py-1">
                        <FaFileInvoiceDollar className="mr-1 text-purple-400" size={12} />
                        <span className="font-medium text-sm">{comision.orden_compra_id}</span>
                      </div>

                      {/* Mantenimiento asociado */}
                      {comision.mantenimiento_id && (
                        <div className="inline-flex items-center bg-gray-700 bg-opacity-50 rounded-md px-2 py-1">
                          <FaWrench className="mr-1 text-green-400" size={12} />
                          <span className="font-medium text-sm">{comision.mantenimiento_id}</span>
                        </div>
                      )}
                    </div>
                    
                    <span className="text-sm font-bold text-green-400 flex items-center">
                      <FaDollarSign className="mr-1" size={10} />
                      {comision.monto_comision.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                    </span>
                  </div>

                  {/* Tercera fila: detalles financieros */}
                  <div className="mt-3 space-y-2">
                    {/* Información financiera */}
                    <div className="flex items-center bg-gray-700 bg-opacity-50 rounded-md px-2 py-1">
                      <FaChartLine className="mr-1 text-blue-400" size={12} />
                      <span className="font-medium text-sm mr-2">Financiero:</span>
                      <div className="flex flex-wrap gap-1">
                        <Badge variant="info" size="sm">
                          Venta: ${comision.monto_venta.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                        </Badge>
                        <Badge variant="primary" size="sm">
                          {comision.porcentaje_comision}% comisión
                        </Badge>
                      </div>
                    </div>

                    {/* Repuestos vendidos */}
                    <div className="flex items-center bg-gray-700 bg-opacity-50 rounded-md px-2 py-1">
                      <FaCog className="mr-1 text-orange-400" size={12} />
                      <span className="font-medium text-sm mr-2">Repuestos:</span>
                      <div className="flex flex-wrap gap-1">
                        {comision.items_vendidos.slice(0, 2).map((item, index) => (
                          <Badge key={index} variant="warning" size="sm">
                            {item.nombre} ({item.cantidad}x)
                          </Badge>
                        ))}
                        {comision.items_vendidos.length > 2 && (
                          <Badge variant="secondary" size="sm">
                            +{comision.items_vendidos.length - 2} más
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Información de pago */}
                    {comision.estado_pago === 'pagado' && comision.fecha_pago && (
                      <div className="flex items-center bg-green-900/20 rounded-md px-2 py-1">
                        <FaCheck className="mr-1 text-green-400" size={12} />
                        <span className="font-medium text-sm mr-2 text-green-200">Pago:</span>
                        <div className="flex flex-wrap gap-1">
                          <Badge variant="success" size="sm">
                            {formatFecha(comision.fecha_pago)}
                          </Badge>
                          {comision.numero_factura && (
                            <Badge variant="success" size="sm">
                              {comision.numero_factura}
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Cuarta fila: items y acciones */}
                  <div className="flex justify-between items-center mt-3">
                    <div className="flex items-center space-x-2">
                      <Badge variant="secondary" size="sm">
                        <FaBox className="mr-1" size={10} />
                        {comision.items_vendidos.length} item{comision.items_vendidos.length > 1 ? 's' : ''}
                      </Badge>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleVerDetalle(comision)}
                        icon={<FaEye />}
                      >
                        Ver
                      </Button>
                      
                      {comision.numero_factura && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleExportarReporte('factura')}
                          icon={<FaDownload />}
                        >
                          PDF
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {filteredComisiones.length === 0 && !loading && (
        <div className="text-center py-12">
          <FaDollarSign className="mx-auto text-6xl text-gray-600 mb-4" />
          <h3 className="text-xl font-semibold mb-2">No se encontraron comisiones</h3>
          <p className="text-gray-400">Intenta ajustar los filtros de búsqueda</p>
        </div>
      )}

      {/* Modal de detalle */}
      {selectedPeriodo && (
        <DetalleComisionModal
          isOpen={showDetalleModal}
          onClose={() => {
            setShowDetalleModal(false);
            setSelectedPeriodo(null);
          }}
          comision={selectedPeriodo}
        />
      )}
    </div>
  );
};

// Componente del modal de detalle
const DetalleComisionModal = ({ isOpen, onClose, comision }) => {
  if (!comision) return null;

  const formatFecha = (fecha) => {
    return new Date(fecha).toLocaleDateString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Detalle de Comisión ${comision.id}`}
      size="lg"
      headerExtra={
        <Badge 
          variant={comision.estado_pago === 'pagado' ? 'success' : 
                  comision.estado_pago === 'pendiente' ? 'warning' : 'info'}
        >
          {comision.estado_pago === 'pagado' ? 'Pagado' : 
           comision.estado_pago === 'pendiente' ? 'Pendiente' : 'Procesando'}
        </Badge>
      }
    >
      <div className="max-h-[70vh] overflow-y-auto space-y-6">
        {/* Información general */}
        <Card className="bg-gray-800">
          <CardBody className="p-4">
            <h4 className="font-semibold mb-3">Información General</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-400">ID Comisión:</span>
                <p className="font-medium">{comision.id}</p>
              </div>
              <div>
                <span className="text-gray-400">Período:</span>
                <p className="font-medium">{comision.periodo}</p>
              </div>
              <div>
                <span className="text-gray-400">Fecha Venta:</span>
                <p className="font-medium">{formatFecha(comision.fecha_venta)}</p>
              </div>
              <div>
                <span className="text-gray-400">Orden Compra:</span>
                <p className="font-medium">{comision.orden_compra_id}</p>
              </div>
              {comision.mantenimiento_id && (
                <div>
                  <span className="text-gray-400">Mantenimiento:</span>
                  <p className="font-medium">{comision.mantenimiento_id}</p>
                </div>
              )}
              <div>
                <span className="text-gray-400">Proveedor:</span>
                <div className="flex items-center">
                  <img 
                    src={comision.proveedor.logo} 
                    alt={comision.proveedor.nombre}
                    className="w-5 h-5 rounded mr-2"
                  />
                  <span className="font-medium">{comision.proveedor.nombre}</span>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Detalle financiero */}
        <Card className="bg-gray-800">
          <CardBody className="p-4">
            <h4 className="font-semibold mb-3">Detalle Financiero</h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-400">Monto Total de Venta:</span>
                <span className="font-medium">${comision.monto_venta.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Porcentaje de Comisión:</span>
                <span className="font-medium">{comision.porcentaje_comision}%</span>
              </div>
              <div className="border-t border-gray-600 pt-3">
                <div className="flex justify-between">
                  <span className="text-lg font-semibold">Comisión Ganada:</span>
                  <span className="text-lg font-semibold text-green-400">
                    ${comision.monto_comision.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                  </span>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Items vendidos */}
        <Card className="bg-gray-800">
          <CardBody className="p-4">
            <h4 className="font-semibold mb-3">Repuestos Vendidos</h4>
            <div className="space-y-3">
              {comision.items_vendidos.map((item, index) => (
                <div key={index} className="flex justify-between items-center p-3 bg-gray-700 rounded">
                  <div>
                    <p className="font-medium">{item.nombre}</p>
                    <p className="text-sm text-gray-400">Cantidad: {item.cantidad}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">${(item.precio_unitario * item.cantidad).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}</p>
                    <p className="text-sm text-gray-400">${item.precio_unitario.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')} c/u</p>
                  </div>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>

        {/* Estado de pago */}
        {comision.estado_pago === 'pagado' && comision.fecha_pago && (
          <Card className="bg-green-900/20 border-green-800/50">
            <CardBody className="p-4">
              <h4 className="font-semibold mb-3 text-green-200">Información de Pago</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-green-300">Fecha de Pago:</span>
                  <span className="font-medium text-white">{formatFecha(comision.fecha_pago)}</span>
                </div>
                {comision.metodo_pago && (
                  <div className="flex justify-between">
                    <span className="text-green-300">Método de Pago:</span>
                    <span className="font-medium text-white">
                      {comision.metodo_pago === 'transferencia_bancaria' ? 'Transferencia Bancaria' : comision.metodo_pago}
                    </span>
                  </div>
                )}
                {comision.numero_factura && (
                  <div className="flex justify-between">
                    <span className="text-green-300">Número de Factura:</span>
                    <span className="font-medium text-white">{comision.numero_factura}</span>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        )}

        {/* Botón de cerrar */}
        <div className="flex justify-end">
          <Button variant="primary" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ComisionesProveedorDashboard;